import { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { FeatureGuard } from '@/components/feature-guard.tsx';
import { Footer } from '@/components/footer.tsx';
import { OfficeAppShell } from '@/components/office-app-shell.tsx';
import { RouteErrorBoundary } from '@/components/route-error-boundary.tsx';

import { AppRoute } from '@/constants/app-routes.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import { LazyDashboardView } from '@/pages/lazy-pages.ts';
import { LoadingPage } from '@/pages/loading-page.tsx';

import { getFooterLinks } from '@/utils/footer-utils.ts';

export const AuthenticatedLayout = () => {
    const { pathname } = useLocation();
    const isCareView = pathname.replace(/\/+$/, '') === `${AppRoute.CAREVIEW}`;

    // Latch that stays true once CareView is visited, so we defer mounting the
    // QuickSight host (and its embed session) until the user actually goes there.
    const [hasVisitedCareView, setHasVisitedCareView] = useState(false);
    if (isCareView && !hasVisitedCareView) setHasVisitedCareView(true);

    return (
        <OfficeAppShell>
            {/*
                Persistent host — once CareView has been visited it stays mounted
                and never unmounts while we remain logged in. We hide it using CSS,
                so the QuickSight iframe remains in the DOM (no reload).
            */}
            {hasVisitedCareView && (
                <div className={isCareView ? 'h-full' : 'hidden'}>
                    <RouteErrorBoundary routeName="careview">
                        <Suspense fallback={<LoadingPage />}>
                            <FeatureGuard flag={FeatureFlag.IS_CAREVIEW_ENABLED} featureName="CareView">
                                <LazyDashboardView />
                            </FeatureGuard>
                        </Suspense>
                    </RouteErrorBoundary>
                </div>
            )}

            {/* The remaining routes are rendered normally */}
            {!isCareView && <Outlet />}

            {/* Single footer for every page; links depend on the current page */}
            <Footer links={getFooterLinks(pathname)} />
        </OfficeAppShell>
    );
};
